import axios from "axios";
import { reportClientHealth } from "./clientHealth";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AI_REFERRER_DOMAINS = [
  ["chatgpt", "chatgpt.com"],
  ["chatgpt", "chat.openai.com"],
  ["claude", "claude.ai"],
  ["perplexity", "perplexity.ai"],
  ["gemini", "gemini.google.com"],
  ["copilot", "copilot.microsoft.com"],
  ["manus", "manus.im"],
  ["meta_ai", "meta.ai"],
  ["you_com", "you.com"],
  ["phind", "phind.com"],
  ["poe", "poe.com"],
];

const matchesDomain = (host, domain) =>
  host === domain || host.endsWith(`.${domain}`);

export const getAIReferralSource = () => {
  try {
    const stored = globalThis.sessionStorage?.getItem("vb_ai_referral");
    const allowed = new Set(AI_REFERRER_DOMAINS.map(([source]) => source));
    if (stored && allowed.has(stored)) return stored;

    const params = new URLSearchParams(
      globalThis.location?.search || ""
    );

    if (
      params.get("utm_source")?.toLowerCase() ===
      "chatgpt.com"
    ) {
      globalThis.sessionStorage?.setItem(
        "vb_ai_referral",
        "chatgpt"
      );
      return "chatgpt";
    }

    const referrer = globalThis.document?.referrer;
    if (!referrer) return null;
    const host = new URL(referrer).hostname.toLowerCase().replace(/\.$/, "");
    const match = AI_REFERRER_DOMAINS.find(([, domain]) =>
      matchesDomain(host, domain)
    );
    if (!match) return null;
    globalThis.sessionStorage?.setItem("vb_ai_referral", match[0]);
    return match[0];
  } catch {
    return null;
  }
};

// Persistent booking session id (used for draft recovery)
export const getSessionId = () => {
  let s = localStorage.getItem("vb_session");
  if (!s) {
    const secureCrypto = globalThis.crypto;
    if (!secureCrypto) throw new Error("Secure browser randomness is unavailable.");
    if (typeof secureCrypto.randomUUID === "function") {
      s = "vb-" + secureCrypto.randomUUID();
    } else if (typeof secureCrypto.getRandomValues === "function") {
      const bytes = new Uint8Array(32);
      secureCrypto.getRandomValues(bytes);
      s = "vb-" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    } else {
      throw new Error("Secure browser randomness is unavailable.");
    }
    localStorage.setItem("vb_session", s);
  }
  return s;
};

export const api = axios.create({ baseURL: BASE, timeout: 20000, withCredentials: true });

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-Booking-Session"] = getSessionId();
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error?.config || {};
    const path = config.url || "";
    const status = error?.response?.status;
    const isReporterRequest = path.includes("/ops/client-errors");
    const isTimeout = error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "");
    const isNetworkFailure = !error?.response;
    const isServerFailure = Number(status) >= 500;

    if (!isReporterRequest && (isTimeout || isNetworkFailure || isServerFailure)) {
      reportClientHealth({
        kind: isTimeout ? "request_timeout" : isNetworkFailure ? "network_error" : "server_error",
        message: error?.message || `Request failed with status ${status}`,
        action: `${String(config.method || "request").toUpperCase()} request`,
        component: "api",
        request_path: path,
        status_code: Number.isInteger(status) ? status : null,
      });
    }

    return Promise.reject(error);
  },
);

// Map FastAPI error detail (string | array | object) to a display string
export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const publicApi = {
  content: () => api.get("/public/content").then((r) => r.data),
  submitInquiry: (payload) => api.post("/public/contact-inquiry", payload).then((r) => r.data),
};

export const systemApi = {
  status: () => api.get("/system/status").then((r) => r.data),
  version: () => api.get("/system/version").then((r) => r.data),
};

export const authApi = {
  register: (payload) => api.post("/auth/register", { base_url: window.location.origin, ...payload }).then((r) => r.data),
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  googleSession: (credential) => api.post("/auth/google/session", { credential }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  forgot: (email) => api.post("/auth/forgot-password", { email, base_url: window.location.origin }).then((r) => r.data),
  reset: (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data),
  guestLookup: (email) => api.post("/auth/guest-lookup", { email }).then((r) => r.data),
  activateGuest: (email) => api.post("/auth/activate-guest", { email, base_url: window.location.origin }).then((r) => r.data),
};

export const portalApi = {
  dashboard: () => api.get("/portal/dashboard").then((r) => r.data),
  reservations: (scope = "all") => api.get("/portal/reservations", { params: { scope } }).then((r) => r.data),
  reservation: (rid) => api.get(`/portal/reservations/${rid}`).then((r) => r.data),
  driverLocation: (rid) => api.get(`/portal/reservations/${rid}/driver-location`).then((r) => r.data),
  sharePickupPoint: (rid, payload) => api.post(`/portal/reservations/${rid}/customer-pickup-point`, payload).then((r) => r.data),
  invoice: (rid) => api.get(`/portal/reservations/${rid}/invoice`).then((r) => r.data),
  modificationQuote: (rid, payload) =>
    api.post(`/reservations/${rid}/modifications/quote`, payload).then((r) => r.data),
  modification: (rid, quoteId) =>
    api.get(`/reservations/${rid}/modifications/${quoteId}`).then((r) => r.data),
  acceptModification: (rid, quoteId, idempotencyKey) =>
    api.post(
      `/reservations/${rid}/modifications/${quoteId}/accept`,
      { idempotency_key: idempotencyKey }
    ).then((r) => r.data),
  cancelModification: (rid, quoteId) =>
    api.post(`/reservations/${rid}/modifications/${quoteId}/cancel`).then((r) => r.data),
  rebook: (rid) => api.post(`/portal/reservations/${rid}/rebook`, { session_id: getSessionId() }).then((r) => r.data),
  updateProfile: (payload) => api.put("/portal/profile", payload).then((r) => r.data),
  updatePrefs: (payload) => api.put("/portal/communication-preferences", payload).then((r) => r.data),
  accountDeletionStatus: () => api.get("/portal/account-deletion").then((r) => r.data),
  requestAccountDeletion: (payload) => api.post("/portal/account-deletion", payload).then((r) => r.data),
  cancelAccountDeletion: () => api.delete("/portal/account-deletion").then((r) => r.data),
  addresses: () => api.get("/portal/addresses").then((r) => r.data),
  addAddress: (payload) => api.post("/portal/addresses", payload).then((r) => r.data),
  deleteAddress: (id) => api.delete(`/portal/addresses/${id}`).then((r) => r.data),
  favorites: () => api.get("/portal/favorites").then((r) => r.data),
  addFavorite: (payload) => api.post("/portal/favorites", payload).then((r) => r.data),
  deleteFavorite: (id) => api.delete(`/portal/favorites/${id}`).then((r) => r.data),
  bookFavorite: (id) => api.post(`/portal/favorites/${id}/book`, { session_id: getSessionId() }).then((r) => r.data),
  loyalty: () => api.get("/portal/loyalty").then((r) => r.data),
  paymentMethods: () => api.get("/portal/payment-methods").then((r) => r.data),
  setupIntent: () => api.post("/portal/payment-methods/setup-intent").then((r) => r.data),
  deletePaymentMethod: (id) => api.delete(`/portal/payment-methods/${id}`).then((r) => r.data),
};

export const adminApi = {
  customers: (q) => api.get("/admin/customers", { params: q ? { q } : {} }).then((r) => r.data),
  customer: (id) => api.get(`/admin/customers/${id}`).then((r) => r.data),
  setStatus: (id, status) => api.put(`/admin/customers/${id}/status`, { status }).then((r) => r.data),
  triggerReset: (id) => api.post(`/admin/customers/${id}/reset-password`, { base_url: window.location.origin }).then((r) => r.data),
  setPrefs: (id, prefs) => api.put(`/admin/customers/${id}/communication-preferences`, prefs).then((r) => r.data),
  merge: (source_user_id, target_user_id) => api.post("/admin/customers/merge", { source_user_id, target_user_id }).then((r) => r.data),
  auditLog: () => api.get("/admin/audit-log").then((r) => r.data),
  getLoyalty: () => api.get("/admin/loyalty-config").then((r) => r.data),
  setLoyalty: (payload) => api.put("/admin/loyalty-config", payload).then((r) => r.data),

  // 7A Command Center
  me: () => api.get("/admin/me").then((r) => r.data),
  overview: () => api.get("/admin/overview").then((r) => r.data),
  reservations: (params) => api.get("/admin/reservations", { params }).then((r) => r.data),
  reservationDetail: (rid) => api.get(`/admin/reservations/${rid}`).then((r) => r.data),
  markReservationViewed: (rid) => api.post(`/admin/reservations/${rid}/viewed`).then((r) => r.data),
  removeReservation: (rid, payload) => api.post(`/admin/reservations/${rid}/remove`, payload).then((r) => r.data),
  overridePrice: (rid, total, reason) => api.post(`/admin/reservations/${rid}/override-price`, { total, reason }).then((r) => r.data),
  payments: (params) => api.get("/admin/payments", { params }).then((r) => r.data),
  customerFlags: (id, flags) => api.put(`/admin/customers/${id}/flags`, flags).then((r) => r.data),
  staff: () => api.get("/admin/staff").then((r) => r.data),
  createStaff: (payload) => api.post("/admin/staff", payload).then((r) => r.data),
  setStaffRole: (id, admin_role) => api.put(`/admin/staff/${id}/role`, { admin_role }).then((r) => r.data),
  removeStaff: (id) => api.delete(`/admin/staff/${id}`).then((r) => r.data),
  rolesConfig: () => api.get("/admin/roles-config").then((r) => r.data),
  setRolesConfig: (roles) => api.put("/admin/roles-config", { roles }).then((r) => r.data),
  featureFlags: () => api.get("/admin/feature-flags").then((r) => r.data),
  setFeatureFlags: (payload) => api.put("/admin/feature-flags", payload).then((r) => r.data),
  systemSettings: () => api.get("/admin/system-settings").then((r) => r.data),
  setSystemSettings: (payload) => api.put("/admin/system-settings", payload).then((r) => r.data),
  activity: (params) => api.get("/admin/activity", { params }).then((r) => r.data),
  // reused payment actions
  refundPayment: (pid, selection, reason) => api.post(`/payments/${pid}/refund`, { ...selection, reason, refunded_by: "admin" }).then((r) => r.data),
  // reused pricing config
  pricingConfig: () => api.get("/pricing/config").then((r) => r.data),
  setPricingConfig: (payload) => api.put("/pricing/config", payload).then((r) => r.data),
  vehiclesConfig: () => api.get("/pricing/vehicles-config").then((r) => r.data),
  setVehicle: (vid, payload) => api.put(`/pricing/vehicles/${vid}`, payload).then((r) => r.data),
  promoCodes: () => api.get("/pricing/promo-codes").then((r) => r.data),
  addPromo: (payload) => api.post("/pricing/promo-codes", payload).then((r) => r.data),
  updatePromo: (pid, payload) => api.put(`/pricing/promo-codes/${pid}`, payload).then((r) => r.data),
  deletePromo: (pid) => api.delete(`/pricing/promo-codes/${pid}`).then((r) => r.data),
  paymentSettings: () => api.get("/payments/settings").then((r) => r.data),
  setPaymentSettings: (payload) => api.put("/payments/settings", payload).then((r) => r.data),
  // reservation admin actions (backend gates valid transitions)
  setReservationStatus: (rid, status, reason) => api.post(`/reservations/${rid}/status`, { status, reason, changed_by: "admin" }).then((r) => r.data),
  addReservationNote: (rid, note) => api.post(`/reservations/${rid}/notes`, { note, author: "admin" }).then((r) => r.data),

  // 7B Fleet & Operations
  fleetVehicles: () => api.get("/admin/fleet/vehicles").then((r) => r.data),
  fleetCreateVehicle: (payload) => api.post("/admin/fleet/vehicles", payload).then((r) => r.data),
  fleetUpdateVehicle: (vid, patch) => api.put(`/admin/fleet/vehicles/${vid}`, patch).then((r) => r.data),
  fleetDeleteVehicle: (vid) => api.delete(`/admin/fleet/vehicles/${vid}`).then((r) => r.data),
  fleetChauffeurs: (params) => api.get("/admin/fleet/chauffeurs", { params }).then((r) => r.data),
  fleetCreateChauffeur: (payload) => api.post("/admin/fleet/chauffeurs", payload).then((r) => r.data),
  fleetUpdateChauffeur: (cid, patch) => api.put(`/admin/fleet/chauffeurs/${cid}`, patch).then((r) => r.data),
  fleetDeleteChauffeur: (cid) => api.delete(`/admin/fleet/chauffeurs/${cid}`).then((r) => r.data),
  fleetCompliance: (days = 30) => api.get("/admin/fleet/compliance-alerts", { params: { days } }).then((r) => r.data),
  fleetMedia: (category) => api.get("/admin/fleet/media", { params: category ? { category } : {} }).then((r) => r.data),
  fleetDeleteFile: (id) => api.delete(`/admin/fleet/files/${id}`).then((r) => r.data),
  fleetUpload: (file, category, extra = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    if (extra.owner_id) fd.append("owner_id", extra.owner_id);
    if (extra.public) fd.append("public", "true");
    return api.post("/admin/fleet/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  fileUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}`,
  fleetDispatchBoard: (day) => api.get("/admin/fleet/dispatch/board", { params: day ? { day } : {} }).then((r) => r.data),
  fleetAssign: (rid, payload) => api.post(`/admin/fleet/dispatch/${rid}/assign`, payload).then((r) => r.data),
  fleetOverrideTripStatus: (rid, status, reason) => api.post(`/admin/fleet/dispatch/${rid}/override-status`, { status, reason }).then((r) => r.data),
  fleetOverrideDriverAvailability: (cid, state, reason) => api.post(`/admin/fleet/dispatch/chauffeurs/${cid}/availability`, { state, reason }).then((r) => r.data),
  fleetCalendar: (start, end, filters = {}) => api.get("/admin/fleet/calendar", { params: { start, end, ...filters } }).then((r) => r.data),

  // 7C Financials
  commissionConfig: () => api.get("/admin/financials/commission/config").then((r) => r.data),
  setCommissionConfig: (payload) => api.put("/admin/financials/commission/config", payload).then((r) => r.data),
  commissionPreview: (rid) => api.get(`/admin/financials/commission/preview/${rid}`).then((r) => r.data),
  reportsSummary: (days = 30) => api.get("/admin/financials/reports/summary", { params: { days } }).then((r) => r.data),
  reportsVehicleUtilization: (days = 30) => api.get("/admin/financials/reports/vehicle-utilization", { params: { days } }).then((r) => r.data),
  reportsChauffeurPay: (days = 30) => api.get("/admin/financials/reports/chauffeur-pay", { params: { days } }).then((r) => r.data),
  analyticsTimeseries: (days = 30) => api.get("/admin/financials/analytics/timeseries", { params: { days } }).then((r) => r.data),
  exportUrl: (entity, fmt = "csv") => `${process.env.REACT_APP_BACKEND_URL}/api/admin/financials/export/${entity}?fmt=${fmt}`,
  importEntity: (entity, file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post(`/admin/financials/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },

  // 7D Content & Communications
  websiteContent: () => api.get("/admin/content/website").then((r) => r.data),
  setWebsiteContent: (payload) => api.put("/admin/content/website", payload).then((r) => r.data),
  testimonials: () => api.get("/admin/content/testimonials").then((r) => r.data),
  createTestimonial: (payload) => api.post("/admin/content/testimonials", payload).then((r) => r.data),
  updateTestimonial: (tid, patch) => api.put(`/admin/content/testimonials/${tid}`, patch).then((r) => r.data),
  deleteTestimonial: (tid) => api.delete(`/admin/content/testimonials/${tid}`).then((r) => r.data),
  faqList: () => api.get("/admin/content/faq").then((r) => r.data),
  createFaq: (payload) => api.post("/admin/content/faq", payload).then((r) => r.data),
  updateFaq: (fid, patch) => api.put(`/admin/content/faq/${fid}`, patch).then((r) => r.data),
  deleteFaq: (fid) => api.delete(`/admin/content/faq/${fid}`).then((r) => r.data),
  templates: (channel) => api.get("/admin/content/templates", { params: channel ? { channel } : {} }).then((r) => r.data),
  updateTemplate: (tid, patch) => api.put(`/admin/content/templates/${tid}`, patch).then((r) => r.data),
  previewTemplate: (tid, variables) => api.post(`/admin/content/templates/${tid}/preview`, { variables }).then((r) => r.data),
  automations: () => api.get("/admin/content/automations").then((r) => r.data),
  createAutomation: (payload) => api.post("/admin/content/automations", payload).then((r) => r.data),
  updateAutomation: (aid, patch) => api.put(`/admin/content/automations/${aid}`, patch).then((r) => r.data),
  deleteAutomation: (aid) => api.delete(`/admin/content/automations/${aid}`).then((r) => r.data),

  // Contact Inquiries (Admin) — public form target lives at /api/public/contact-inquiry
  inquiries: (params) => api.get("/admin/content/inquiries", { params }).then((r) => r.data),
  updateInquiry: (iid, patch) => api.put(`/admin/content/inquiries/${iid}`, patch).then((r) => r.data),
  moveInquiryStage: (iid, pipeline_stage) => api.patch(`/admin/content/inquiries/${iid}/stage`, { pipeline_stage }).then((r) => r.data),
  setInquiryFollowUp: (iid, patch) => api.patch(`/admin/content/inquiries/${iid}/follow-up`, patch).then((r) => r.data),
  logInquiryActivity: (iid, type, note) => api.post(`/admin/content/inquiries/${iid}/activity`, { type, note }).then((r) => r.data),
  followUpQueue: () => api.get(`/admin/content/inquiries/follow-ups`).then((r) => r.data),
  corporateAnalytics: (days) => api.get(`/admin/content/inquiries/analytics/corporate`, { params: days ? { days } : {} }).then((r) => r.data),
  addInquiryNote: (iid, text) => api.post(`/admin/content/inquiries/${iid}/notes`, { text }).then((r) => r.data),
  deleteInquiry: (iid) => api.delete(`/admin/content/inquiries/${iid}`).then((r) => r.data),
  inquiryExportUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/content/inquiries/export${qs ? "?" + qs : ""}`;
  },

  // 7E Advanced
  geofences: () => api.get("/admin/advanced/geofences").then((r) => r.data),
  createGeofence: (payload) => api.post("/admin/advanced/geofences", payload).then((r) => r.data),
  updateGeofence: (gid, patch) => api.put(`/admin/advanced/geofences/${gid}`, patch).then((r) => r.data),
  deleteGeofence: (gid) => api.delete(`/admin/advanced/geofences/${gid}`).then((r) => r.data),
  businessRules: () => api.get("/admin/advanced/business-rules").then((r) => r.data),
  setBusinessRules: (patch) => api.put("/admin/advanced/business-rules", patch).then((r) => r.data),
  holidays: () => api.get("/admin/advanced/holidays").then((r) => r.data),
  createHoliday: (payload) => api.post("/admin/advanced/holidays", payload).then((r) => r.data),
  updateHoliday: (hid, patch) => api.put(`/admin/advanced/holidays/${hid}`, patch).then((r) => r.data),
  deleteHoliday: (hid) => api.delete(`/admin/advanced/holidays/${hid}`).then((r) => r.data),
  apiTokens: () => api.get("/admin/advanced/api-tokens").then((r) => r.data),
  createApiToken: (payload) => api.post("/admin/advanced/api-tokens", payload).then((r) => r.data),
  revokeApiToken: (tid) => api.delete(`/admin/advanced/api-tokens/${tid}`).then((r) => r.data),
  systemHealth: () => api.get("/admin/advanced/system-health").then((r) => r.data),
  createBackup: () => api.post("/admin/advanced/backup").then((r) => r.data),
  backups: () => api.get("/admin/advanced/backups").then((r) => r.data),
  backupDownloadUrl: (bid) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/advanced/backups/${bid}/download`,
  deleteBackup: (bid) => api.delete(`/admin/advanced/backups/${bid}`).then((r) => r.data),
  globalSearch: (q) => api.get("/admin/advanced/search", { params: { q } }).then((r) => r.data),

  // 6A Corporate Portal foundations
  corpRolesConfig: () => api.get("/admin/corporate/roles-config").then((r) => r.data),
  setCorpRolesConfig: (roles) => api.put("/admin/corporate/roles-config", { roles }).then((r) => r.data),
  companies: (params) => api.get("/admin/corporate/companies", { params }).then((r) => r.data),
  company: (cid) => api.get(`/admin/corporate/companies/${cid}`).then((r) => r.data),
  createCompany: (payload) => api.post("/admin/corporate/companies", payload).then((r) => r.data),
  updateCompany: (cid, patch) => api.put(`/admin/corporate/companies/${cid}`, patch).then((r) => r.data),
  archiveCompany: (cid) => api.delete(`/admin/corporate/companies/${cid}`).then((r) => r.data),
  companyFeatures: (cid) => api.get(`/admin/corporate/companies/${cid}/features`).then((r) => r.data),
  setCompanyFeatures: (cid, patch) => api.put(`/admin/corporate/companies/${cid}/features`, patch).then((r) => r.data),
  companyDepartments: (cid) => api.get(`/admin/corporate/companies/${cid}/departments`).then((r) => r.data),
  createDepartment: (cid, payload) => api.post(`/admin/corporate/companies/${cid}/departments`, payload).then((r) => r.data),
  updateDepartment: (did, patch) => api.put(`/admin/corporate/departments/${did}`, patch).then((r) => r.data),
  deleteDepartment: (did) => api.delete(`/admin/corporate/departments/${did}`).then((r) => r.data),
  companyCostCenters: (cid) => api.get(`/admin/corporate/companies/${cid}/cost-centers`).then((r) => r.data),
  createCostCenter: (cid, payload) => api.post(`/admin/corporate/companies/${cid}/cost-centers`, payload).then((r) => r.data),
  deleteCostCenter: (ccid) => api.delete(`/admin/corporate/cost-centers/${ccid}`).then((r) => r.data),
  companyProjects: (cid) => api.get(`/admin/corporate/companies/${cid}/projects`).then((r) => r.data),
  createProject: (cid, payload) => api.post(`/admin/corporate/companies/${cid}/projects`, payload).then((r) => r.data),
  deleteProject: (pid) => api.delete(`/admin/corporate/projects/${pid}`).then((r) => r.data),
  companyOffices: (cid) => api.get(`/admin/corporate/companies/${cid}/offices`).then((r) => r.data),
  createOffice: (cid, payload) => api.post(`/admin/corporate/companies/${cid}/offices`, payload).then((r) => r.data),
  updateOffice: (oid, patch) => api.put(`/admin/corporate/offices/${oid}`, patch).then((r) => r.data),
  deleteOffice: (oid) => api.delete(`/admin/corporate/offices/${oid}`).then((r) => r.data),
  companyEmployees: (cid, params) => api.get(`/admin/corporate/companies/${cid}/employees`, { params }).then((r) => r.data),
  createEmployee: (cid, payload) => api.post(`/admin/corporate/companies/${cid}/employees`, payload).then((r) => r.data),
  updateEmployee: (eid, patch) => api.put(`/admin/corporate/employees/${eid}`, patch).then((r) => r.data),
  deleteEmployee: (eid) => api.delete(`/admin/corporate/employees/${eid}`).then((r) => r.data),
  importEmployees: (cid, file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post(`/admin/corporate/companies/${cid}/employees/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  employeeExportUrl: (cid) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/corporate/companies/${cid}/employees/export`,
  corpAudit: (company_id) => api.get("/admin/corporate/audit", { params: company_id ? { company_id } : {} }).then((r) => r.data),

  // 6B Travelers / Guests / Hierarchy
  companyTravelers: (cid, params) => api.get(`/admin/corporate/companies/${cid}/travelers`, { params }).then((r) => r.data),
  createTraveler: (cid, payload) => api.post(`/admin/corporate/companies/${cid}/travelers`, payload).then((r) => r.data),
  updateTraveler: (tid, patch) => api.put(`/admin/corporate/travelers/${tid}`, patch).then((r) => r.data),
  deleteTraveler: (tid) => api.delete(`/admin/corporate/travelers/${tid}`).then((r) => r.data),
  travelerHistory: (tid) => api.get(`/admin/corporate/travelers/${tid}/history`).then((r) => r.data),
  bookingAuthorize: (cid, payload) => api.post(`/admin/corporate/companies/${cid}/booking/authorize`, payload).then((r) => r.data),
  guestInvites: (cid) => api.get(`/admin/corporate/companies/${cid}/guest-invites`).then((r) => r.data),
  createGuestInvite: (cid, payload) => api.post(`/admin/corporate/companies/${cid}/guest-invites`, payload).then((r) => r.data),
  revokeGuestInvite: (iid) => api.delete(`/admin/corporate/guest-invites/${iid}`).then((r) => r.data),
  companyTree: (cid) => api.get(`/admin/corporate/companies/${cid}/tree`).then((r) => r.data),
  companySubsidiaries: (cid) => api.get(`/admin/corporate/companies/${cid}/subsidiaries`).then((r) => r.data),
  setEmployeeCanBookFor: (eid, executive_employee_ids) => api.put(`/admin/corporate/employees/${eid}/can-book-for`, { executive_employee_ids }).then((r) => r.data),

  // 6C Policies, Workflows, Budgets, Simulator
  companyPolicies: (cid) => api.get(`/admin/corporate/companies/${cid}/policies`).then((r) => r.data),
  createPolicy: (cid, p) => api.post(`/admin/corporate/companies/${cid}/policies`, p).then((r) => r.data),
  updatePolicy: (pid, p) => api.put(`/admin/corporate/policies/${pid}`, p).then((r) => r.data),
  deletePolicy: (pid) => api.delete(`/admin/corporate/policies/${pid}`).then((r) => r.data),
  companyWorkflows: (cid) => api.get(`/admin/corporate/companies/${cid}/workflows`).then((r) => r.data),
  createWorkflow: (cid, p) => api.post(`/admin/corporate/companies/${cid}/workflows`, p).then((r) => r.data),
  updateWorkflow: (wid, p) => api.put(`/admin/corporate/workflows/${wid}`, p).then((r) => r.data),
  deleteWorkflow: (wid) => api.delete(`/admin/corporate/workflows/${wid}`).then((r) => r.data),
  companyBudgets: (cid) => api.get(`/admin/corporate/companies/${cid}/budgets`).then((r) => r.data),
  createBudget: (cid, p) => api.post(`/admin/corporate/companies/${cid}/budgets`, p).then((r) => r.data),
  updateBudget: (bid, p) => api.put(`/admin/corporate/budgets/${bid}`, p).then((r) => r.data),
  deleteBudget: (bid) => api.delete(`/admin/corporate/budgets/${bid}`).then((r) => r.data),
  companyLimits: (cid) => api.get(`/admin/corporate/companies/${cid}/spending-limits`).then((r) => r.data),
  createLimit: (cid, p) => api.post(`/admin/corporate/companies/${cid}/spending-limits`, p).then((r) => r.data),
  deleteLimit: (lid) => api.delete(`/admin/corporate/spending-limits/${lid}`).then((r) => r.data),
  policySimulate: (payload) => api.post("/admin/corporate/policy-simulate", payload).then((r) => r.data),

  // 6C+ Delegations
  companyDelegations: (cid, params) => api.get(`/admin/corporate/companies/${cid}/delegations`, { params }).then((r) => r.data),
  createDelegation: (cid, p) => api.post(`/admin/corporate/companies/${cid}/delegations`, p).then((r) => r.data),
  revokeDelegation: (did) => api.delete(`/admin/corporate/delegations/${did}`).then((r) => r.data),

  // 6C+ Emergency Overrides
  emergencyOverride: (p) => api.post("/admin/corporate/emergency-override", p).then((r) => r.data),
  companyOverrides: (cid, params) => api.get(`/admin/corporate/companies/${cid}/emergency-overrides`, { params }).then((r) => r.data),

  // 6C+ Policy Version History
  policyVersions: (pid) => api.get(`/admin/corporate/policies/${pid}/versions`).then((r) => r.data),

  // 6C+ Corporate Calendar
  companyCalendar: (cid) => api.get(`/admin/corporate/companies/${cid}/calendar`).then((r) => r.data),
  createCalendarEvent: (cid, p) => api.post(`/admin/corporate/companies/${cid}/calendar`, p).then((r) => r.data),
  deleteCalendarEvent: (eid) => api.delete(`/admin/corporate/calendar/${eid}`).then((r) => r.data),

  // 6D Billing Profiles
  companyBillingProfiles: (cid) => api.get(`/admin/corporate/companies/${cid}/billing-profiles`).then((r) => r.data),
  createBillingProfile: (cid, p) => api.post(`/admin/corporate/companies/${cid}/billing-profiles`, p).then((r) => r.data),
  updateBillingProfile: (pid, p) => api.put(`/admin/corporate/billing-profiles/${pid}`, p).then((r) => r.data),
  deleteBillingProfile: (pid) => api.delete(`/admin/corporate/billing-profiles/${pid}`).then((r) => r.data),

  // 6D Purchase Orders
  companyPurchaseOrders: (cid, params) => api.get(`/admin/corporate/companies/${cid}/purchase-orders`, { params }).then((r) => r.data),
  createPurchaseOrder: (cid, p) => api.post(`/admin/corporate/companies/${cid}/purchase-orders`, p).then((r) => r.data),
  updatePurchaseOrder: (poid, p) => api.put(`/admin/corporate/purchase-orders/${poid}`, p).then((r) => r.data),
  deletePurchaseOrder: (poid) => api.delete(`/admin/corporate/purchase-orders/${poid}`).then((r) => r.data),

  // 6D Invoices / Statements
  companyInvoices: (cid, params) => api.get(`/admin/corporate/companies/${cid}/invoices`, { params }).then((r) => r.data),
  generateInvoice: (cid, p) => api.post(`/admin/corporate/companies/${cid}/invoices/generate`, p).then((r) => r.data),
  invoiceGet: (iid) => api.get(`/admin/corporate/invoices/${iid}`).then((r) => r.data),
  invoiceUpdate: (iid, p) => api.put(`/admin/corporate/invoices/${iid}`, p).then((r) => r.data),
  invoicePdfUrl: (iid) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/corporate/invoices/${iid}/pdf`,
  invoiceCsvUrl: (iid) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/corporate/invoices/${iid}/csv`,
  invoiceSendEmail: (iid, p) => api.post(`/admin/corporate/invoices/${iid}/send-email`, p).then((r) => r.data),

  // 6D Payments (reconciliation)
  companyPayments: (cid, params) => api.get(`/admin/corporate/companies/${cid}/payments`, { params }).then((r) => r.data),
  createPayment: (cid, p) => api.post(`/admin/corporate/companies/${cid}/payments`, p).then((r) => r.data),
  deletePayment: (pid) => api.delete(`/admin/corporate/payments/${pid}`).then((r) => r.data),

  // 6D Billing Documents archive
  companyDocuments: (cid, params) => api.get(`/admin/corporate/companies/${cid}/documents`, { params }).then((r) => r.data),
  deleteDocument: (did) => api.delete(`/admin/corporate/documents/${did}`).then((r) => r.data),

  // 6D A/R Dashboard
  arSummaryGlobal: () => api.get(`/admin/corporate/ar-summary`).then((r) => r.data),
  arSummaryCompany: (cid) => api.get(`/admin/corporate/companies/${cid}/ar-summary`).then((r) => r.data),

  // Phase 6 wrap-up: Corporate Portal availability + per-company Preview Mode
  setCorporatePortalSettings: (p) => api.put("/admin/system-settings/corporate-portal", p).then((r) => r.data),
  setCompanyPreviewMode: (cid, on) => api.put(`/admin/corporate/companies/${cid}/preview-mode`, { preview_mode: on }).then((r) => r.data),

  // Phase 8: Driver Management (reuses /admin/fleet/chauffeurs + new endpoints)
  drivers: (params) => api.get("/admin/fleet/chauffeurs", { params }).then((r) => r.data),
  driverCreate: (p) => api.post("/admin/fleet/chauffeurs/create", p).then((r) => r.data),
  driverUpdate: (id, p) => api.put(`/admin/fleet/chauffeurs/${id}`, p).then((r) => r.data),
  driverStatus: (id, status, reason) => api.put(`/admin/fleet/chauffeurs/${id}/status`, { status, reason }).then((r) => r.data),
  driverPriority: (id, p) => api.put(`/admin/fleet/chauffeurs/${id}/priority`, p).then((r) => r.data),
  driverCredentials: (id, p) => api.put(`/admin/fleet/chauffeurs/${id}/credentials`, p).then((r) => r.data),
  driverRemove: (id) => api.delete(`/admin/fleet/chauffeurs/${id}`).then((r) => r.data),
  driverAppendLog: (id, p) => api.post(`/admin/fleet/chauffeurs/${id}/log`, p).then((r) => r.data),
  driversLiveSummary: () => api.get("/admin/fleet/drivers/live-summary").then((r) => r.data),
  assignmentMode: () => api.get("/admin/fleet/assignment-mode").then((r) => r.data),
  setAssignmentMode: (p) => api.put("/admin/fleet/assignment-mode", p).then((r) => r.data),
  driverFeatures: () => api.get("/admin/fleet/drivers/features").then((r) => r.data),
  setDriverFeatures: (p) => api.put("/admin/fleet/drivers/features", p).then((r) => r.data),

  // 6E Corporate Reporting & Diagnostics
  reportKpi: (params) => api.get(`/admin/corporate/reports/kpi`, { params }).then((r) => r.data),  reportTripSummary: (params) => api.get(`/admin/corporate/reports/trip-summary`, { params }).then((r) => r.data),
  reportTripSummaryCsvUrl: (params) => {
    const qs = new URLSearchParams({ ...params, format: "csv" }).toString();
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/corporate/reports/trip-summary?${qs}`;
  },
  reportFinancial: (params) => api.get(`/admin/corporate/reports/financial-summary`, { params }).then((r) => r.data),
  reportCompliance: (params) => api.get(`/admin/corporate/reports/compliance-summary`, { params }).then((r) => r.data),
  reportDiagnostics: (params) => api.get(`/admin/corporate/reports/diagnostics`, { params }).then((r) => r.data),
  reportAuditSearch: (params) => api.get(`/admin/corporate/reports/audit-search`, { params }).then((r) => r.data),
  traceDecision: (body) => api.post(`/admin/corporate/reports/trace-decision`, body).then((r) => r.data),

  // 6D Disputes
  companyDisputes: (cid, params) => api.get(`/admin/corporate/companies/${cid}/disputes`, { params }).then((r) => r.data),
  createDispute: (cid, p) => api.post(`/admin/corporate/companies/${cid}/disputes`, p).then((r) => r.data),
  disputeComment: (did, p) => api.post(`/admin/corporate/disputes/${did}/comments`, p).then((r) => r.data),

  // 6D SLAs
  companySLAs: (cid) => api.get(`/admin/corporate/companies/${cid}/slas`).then((r) => r.data),
  createSLA: (cid, p) => api.post(`/admin/corporate/companies/${cid}/slas`, p).then((r) => r.data),
  deleteSLA: (sid) => api.delete(`/admin/corporate/slas/${sid}`).then((r) => r.data),

  // 6D Document Upload (PO attachments, dispute evidence)
  corporateUpload: (companyId, file, purpose = "attachment") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company_id", companyId);
    fd.append("purpose", purpose);
    return api.post("/admin/corporate/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },

  // Phase 8 additions — Waiting Meter, Route Deviations, active reservation picker
  activeReservations: (limit = 30) => api.get("/admin/fleet/reservations/active", { params: { limit } }).then((r) => r.data),
  waitingMeter: (rid, payload) => api.post(`/admin/fleet/dispatch/${rid}/waiting-meter`, payload).then((r) => r.data),
  deviationConfig: () => api.get("/admin/fleet/deviations/config").then((r) => r.data),
  setDeviationConfig: (payload) => api.put("/admin/fleet/deviations/config", payload).then((r) => r.data),
  deviationList: (params) => api.get("/admin/fleet/deviations", { params }).then((r) => r.data),
  raiseDeviation: (payload) => api.post("/admin/fleet/deviations", payload).then((r) => r.data),
  resolveDeviation: (aid, resolution) => api.put(`/admin/fleet/deviations/${aid}/resolve`, { resolution }).then((r) => r.data),
  addDeviationNote: (aid, note) => api.post(`/admin/fleet/deviations/${aid}/notes`, { note }).then((r) => r.data),

  // Phase 9 — Automation Center
  integrations: () => api.get("/admin/automation/integrations").then((r) => r.data),
  setIntegrations: (payload) => api.put("/admin/automation/integrations", payload).then((r) => r.data),
  webhooks: () => api.get("/admin/automation/webhooks").then((r) => r.data),
  createWebhook: (payload) => api.post("/admin/automation/webhooks", payload).then((r) => r.data),
  updateWebhook: (wid, patch) => api.put(`/admin/automation/webhooks/${wid}`, patch).then((r) => r.data),
  deleteWebhook: (wid) => api.delete(`/admin/automation/webhooks/${wid}`).then((r) => r.data),
  automationJobs: (params) => api.get("/admin/automation/jobs", { params }).then((r) => r.data),
  retryJob: (jid) => api.post(`/admin/automation/jobs/${jid}/retry`).then((r) => r.data),
  cancelJob: (jid) => api.post(`/admin/automation/jobs/${jid}/cancel`).then((r) => r.data),
  emitEvent: (event, payload) => api.post("/admin/automation/emit", { event, payload }).then((r) => r.data),
  automationEvents: (limit = 100) => api.get("/admin/automation/events", { params: { limit } }).then((r) => r.data),
  automationNotifications: (unread = false) => api.get("/admin/automation/notifications", { params: { unread } }).then((r) => r.data),
  markNotificationRead: (nid) => api.post(`/admin/automation/notifications/${nid}/read`).then((r) => r.data),

  // Phase 10 · Cluster A — Security & Access
  securityDashboard: () => api.get("/admin/security/dashboard").then((r) => r.data),
  securitySessions: (params) => api.get("/admin/security/sessions", { params }).then((r) => r.data),
  revokeSession: (session_token) => api.post("/admin/security/sessions/revoke", { session_token }).then((r) => r.data),
  userLogoutAll: (uid) => api.post(`/admin/security/users/${uid}/logout-all`).then((r) => r.data),
  loginHistory: (params) => api.get("/admin/security/login-history", { params }).then((r) => r.data),
  lockedAccounts: () => api.get("/admin/security/locked-accounts").then((r) => r.data),
  unlockAccount: (email) => api.post("/admin/security/unlock", { email }).then((r) => r.data),
  resetUserMfa: (uid) => api.post(`/admin/security/users/${uid}/reset-mfa`).then((r) => r.data),
  setUserDisabled: (user_id, disabled, reason) => api.post("/admin/security/users/disable", { user_id, disabled, reason }).then((r) => r.data),
  securityConfig: () => api.get("/admin/security/config").then((r) => r.data),
  setSecurityConfig: (payload) => api.put("/admin/security/config", payload).then((r) => r.data),
  rateLimits: () => api.get("/admin/security/rate-limits").then((r) => r.data),
  setRateLimits: (payload) => api.put("/admin/security/rate-limits", payload).then((r) => r.data),
  securityEvents: (params) => api.get("/admin/security/events", { params }).then((r) => r.data),
  raiseSecurityEvent: (payload) => api.post("/admin/security/events", payload).then((r) => r.data),
  resolveSecurityEvent: (eid, resolution) => api.put(`/admin/security/events/${eid}/resolve`, { resolution }).then((r) => r.data),
  suspiciousActivity: (window_minutes = 60) => api.get("/admin/security/suspicious-activity", { params: { window_minutes } }).then((r) => r.data),

  // Phase 10 · Cluster B — Operations & Observability
  opsMonitoring: () => api.get("/admin/ops/monitoring").then((r) => r.data),
  opsServices: () => api.get("/admin/ops/services").then((r) => r.data),
  opsPerformance: (window_minutes = 60) => api.get("/admin/ops/performance", { params: { window_minutes } }).then((r) => r.data),
  opsErrors: (params) => api.get("/admin/ops/errors", { params }).then((r) => r.data),
  resolveOpsError: (eid, resolution) => api.put(`/admin/ops/errors/${eid}/resolve`, { resolution }).then((r) => r.data),
  opsLogSources: () => api.get("/admin/ops/log-sources").then((r) => r.data),
  opsLogs: (params) => api.get("/admin/ops/logs", { params }).then((r) => r.data),
  opsConfig: () => api.get("/admin/ops/config").then((r) => r.data),
  setOpsConfig: (payload) => api.put("/admin/ops/config", payload).then((r) => r.data),
  opsRetentionCleanup: () => api.post("/admin/ops/retention/cleanup").then((r) => r.data),

  // Phase 10 · Cluster C1 — Infrastructure Core (Backup + DR + Database)
  // Existing backup CRUD lives under /admin/advanced/* — reuse those:
  //   adminApi.backups(), adminApi.createBackup(), adminApi.downloadBackup(), adminApi.deleteBackup()
  backupConfig: () => api.get("/admin/infra/backups/config").then((r) => r.data),
  setBackupConfig: (payload) => api.put("/admin/infra/backups/config", payload).then((r) => r.data),
  verifyBackup: (bid) => api.post(`/admin/infra/backups/${bid}/verify`).then((r) => r.data),
  restoreBackup: (bid, payload) => api.post(`/admin/infra/backups/${bid}/restore`, payload).then((r) => r.data),
  pruneBackups: () => api.post("/admin/infra/backups/prune").then((r) => r.data),
  restoreHistory: () => api.get("/admin/infra/restores").then((r) => r.data),
  databaseStats: () => api.get("/admin/infra/database/stats").then((r) => r.data),
  databaseCollections: (top = 30) => api.get("/admin/infra/database/collections", { params: { top } }).then((r) => r.data),
  databaseIntegrity: () => api.get("/admin/infra/database/integrity").then((r) => r.data),
  drConfig: () => api.get("/admin/infra/dr/config").then((r) => r.data),
  setDrConfig: (payload) => api.put("/admin/infra/dr/config", payload).then((r) => r.data),
  drReadiness: () => api.get("/admin/infra/dr/readiness").then((r) => r.data),
  drRunbooks: () => api.get("/admin/infra/dr/runbooks").then((r) => r.data),
  createRunbook: (payload) => api.post("/admin/infra/dr/runbooks", payload).then((r) => r.data),
  updateRunbook: (rid, patch) => api.put(`/admin/infra/dr/runbooks/${rid}`, patch).then((r) => r.data),
  deleteRunbook: (rid) => api.delete(`/admin/infra/dr/runbooks/${rid}`).then((r) => r.data),
  drEvents: () => api.get("/admin/infra/dr/events").then((r) => r.data),
  createDrEvent: (payload) => api.post("/admin/infra/dr/events", payload).then((r) => r.data),

  // Cluster C · Iteration 2 — Deployment / SSL / CDN / Maintenance
  infraOpsConfig: () => api.get("/admin/infra/ops-config").then((r) => r.data),
  setInfraOpsConfig: (payload) => api.put("/admin/infra/ops-config", payload).then((r) => r.data),
  checkDomains: () => api.get("/admin/infra/domains/check").then((r) => r.data),
  deployments: (environment) => api.get("/admin/infra/deployments", { params: environment ? { environment } : {} }).then((r) => r.data),
  recordDeployment: (payload) => api.post("/admin/infra/deployments", payload).then((r) => r.data),
  verifyDeployment: (did) => api.post(`/admin/infra/deployments/${did}/verify`).then((r) => r.data),
  rollbackDeployment: (payload) => api.post("/admin/infra/deployments/rollback", payload).then((r) => r.data),
  cdnPurge: (paths) => api.post("/admin/infra/cdn/purge", { paths }).then((r) => r.data),
  cdnActions: () => api.get("/admin/infra/cdn/actions").then((r) => r.data),
  maintenanceStatus: () => api.get("/admin/infra/maintenance/status").then((r) => r.data),
  setMaintenanceMode: (payload) => api.post("/admin/infra/maintenance/mode", payload).then((r) => r.data),
  runHealthCheck: () => api.post("/admin/infra/maintenance/health-check").then((r) => r.data),
  clearCache: () => api.post("/admin/infra/maintenance/cache-clear").then((r) => r.data),
  logCleanup: (payload) => api.post("/admin/infra/maintenance/log-cleanup", payload).then((r) => r.data),
  tempCleanup: () => api.post("/admin/infra/maintenance/temp-cleanup").then((r) => r.data),
  announcements: (active) => api.get("/admin/infra/announcements", { params: active ? { active: true } : {} }).then((r) => r.data),
  publishAnnouncement: (payload) => api.post("/admin/infra/announcements", payload).then((r) => r.data),
  archiveAnnouncement: (aid) => api.post(`/admin/infra/announcements/${aid}/archive`).then((r) => r.data),

  // Cluster D — Analytics, SEO, Media
  analyticsOverview: (days = 30) => api.get("/admin/analytics/overview", { params: { days } }).then((r) => r.data),
  geoAiOverview: (days = 30) => api.get("/admin/analytics/geo-ai", { params: { days } }).then((r) => r.data),
  geoAiCsv: (days = 30) => api.get("/admin/analytics/geo-ai/export.csv", { params: { days }, responseType: "blob" }),
  ga4Config: () => api.get("/admin/analytics/ga4/config").then((r) => r.data),
  setGa4Config: (payload) => api.put("/admin/analytics/ga4/config", payload).then((r) => r.data),
  seoConfig: () => api.get("/admin/seo/config").then((r) => r.data),
  setSeoConfig: (payload) => api.put("/admin/seo/config", payload).then((r) => r.data),
  seoPages: () => api.get("/admin/seo/pages").then((r) => r.data),
  saveSeoPage: (key, payload) => api.put(`/admin/seo/pages/${key}`, payload).then((r) => r.data),
  deleteSeoPage: (key) => api.delete(`/admin/seo/pages/${key}`).then((r) => r.data),
  seoHealth: () => api.get("/admin/seo/health").then((r) => r.data),
  seoRedirects: () => api.get("/admin/seo/redirects").then((r) => r.data),
  createRedirect: (payload) => api.post("/admin/seo/redirects", payload).then((r) => r.data),
  deleteRedirect: (rid) => api.delete(`/admin/seo/redirects/${rid}`).then((r) => r.data),
  mediaQueue: (status) => api.get("/admin/media/queue", { params: status && status !== "all" ? { status } : {} }).then((r) => r.data),
  enqueueImage: (payload) => api.post("/admin/media/queue", payload).then((r) => r.data),
  rebuildImage: (iid) => api.post(`/admin/media/queue/${iid}/rebuild`).then((r) => r.data),

  // Cluster E — Compliance, QA, Feature Flags
  privacyOverview: () => api.get("/admin/compliance/privacy/overview").then((r) => r.data),
  exportRequests: (status) => api.get("/admin/compliance/export-requests", { params: status && status !== "all" ? { status } : {} }).then((r) => r.data),
  createExportRequest: (payload) => api.post("/admin/compliance/export-requests", payload).then((r) => r.data),
  completeExportRequest: (rid) => api.post(`/admin/compliance/export-requests/${rid}/complete`).then((r) => r.data),
  deletionRequests: (status) => api.get("/admin/compliance/deletion-requests", { params: status && status !== "all" ? { status } : {} }).then((r) => r.data),
  createDeletionRequest: (payload) => api.post("/admin/compliance/deletion-requests", payload).then((r) => r.data),
  approveDeletion: (rid, confirm) => api.post(`/admin/compliance/deletion-requests/${rid}/approve`, { confirm }).then((r) => r.data),
  consents: (params) => api.get("/admin/compliance/consents", { params }).then((r) => r.data),
  filesOverview: () => api.get("/admin/compliance/files/overview").then((r) => r.data),
  recordFileScan: (payload) => api.post("/admin/compliance/files/scan", payload).then((r) => r.data),
  continuityRollup: () => api.get("/admin/compliance/continuity").then((r) => r.data),
  qaReports: () => api.get("/admin/compliance/qa/reports").then((r) => r.data),
  infraAudit: (module) => api.get("/admin/compliance/infra-audit", { params: module && module !== "all" ? { module } : {} }).then((r) => r.data),
  featureFlags: () => api.get("/admin/compliance/features").then((r) => r.data),
  setFeatureFlags: (flags) => api.put("/admin/compliance/features", { flags }).then((r) => r.data),
};

export const reservationApi = {
  upsertDraft: (payload) => {
    const referralSource = payload?.referral_source || getAIReferralSource();
    return api.post("/reservations/draft", {
      ...payload,
      session_id: getSessionId(),
      origin_source: "website",
      ...(referralSource ? { referral_source: referralSource } : {}),
    }).then((r) => r.data);
  },
  recover: () => api.get("/reservations/recover", { params: { session_id: getSessionId() } }).then((r) => r.data),
  guestHistory: (scope = "all") => api.get("/guest/reservations", { params: { scope } }).then((r) => r.data),
  validate: (step, data) => api.post("/reservations/validate", { step, data }).then((r) => r.data),
  confirm: (rid) => api.post(`/reservations/${rid}/confirm`).then((r) => r.data),
  cancel: (rid, reason) => api.post(`/reservations/${rid}/cancel`, { reason }).then((r) => r.data),
  get: (rid) => api.get(`/reservations/${rid}`).then((r) => r.data),
  vehicles: () => api.get("/vehicles").then((r) => r.data),
  businessRules: () => api.get("/settings/business-rules").then((r) => r.data),
};

export const pricingApi = {
  config: () => api.get("/pricing/config").then((r) => r.data),
  price: (rid, body = {}) => api.post(`/pricing/reservations/${rid}/price`, body).then((r) => r.data),
  quote: (reservation, promo_code) => api.post("/pricing/quote", { reservation, promo_code }).then((r) => r.data),
};

export const paymentApi = {
  settings: () => api.get("/payments/settings").then((r) => r.data),
  setGratuity: (reservation_id, selection) =>
    api.put(`/payments/reservation/${reservation_id}/gratuity`, selection).then((r) => r.data),
  createIntent: (reservation_id, mode = "full") =>
    api.post("/payments/intent", { reservation_id, mode }).then((r) => r.data),
  payWithSavedCard: (reservation_id, payment_method_id, mode = "full") =>
    api.post("/payments/intent-saved", { reservation_id, payment_method_id, mode }).then((r) => r.data),
  get: (pid) => api.get(`/payments/${pid}`).then((r) => r.data),
};

export const money = (amt, cur = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(amt || 0);
